import React from 'react';
import type {IconName} from '@fortawesome/fontawesome-common-types';

import {Text} from '@albert-io/atomic';

import {AdaptiveSubjectLinks} from './AdaptiveSubjectLinks';
import {EmptyState, EmptyIllustration} from './EmptyState';
import {useAdaptiveEmptyStateSubjects} from './useAdaptiveEmptyStateSubjects';

const DIAGNOSTIC_ICON: IconName = 'file-contract';
const SKILL_PATH_ICON: IconName = 'diagram-project';

/**
 * Which of the adaptive lists (or both) has nothing in it. Only the copy and the ghost-row icons
 * differ — every variant sends the teacher to the same place, since an adaptive subject is where a
 * diagnostic and a skill path both start.
 */
export type AdaptiveEmptyVariant = 'skillPaths' | 'diagnostics' | 'both';

const ADAPTIVE_PITCH =
  "Not sure where your students stand? Assign a diagnostic to measure each student's proficiency across a range of skills and then build personalized skill paths from the results. Pick a subject to start.";

const VARIANT_COPY: Record<
  AdaptiveEmptyVariant,
  {title: string; body: string; icon: IconName; frontIcon?: IconName}
> = {
  skillPaths: {
    title: 'No Skill Paths',
    body: 'Send skills from an adaptive subject to build a skill path for your students.',
    icon: SKILL_PATH_ICON
  },
  diagnostics: {
    title: 'No Diagnostics Yet!',
    body: ADAPTIVE_PITCH,
    icon: DIAGNOSTIC_ICON
  },
  both: {
    title: 'No Diagnostics or Skill Paths',
    body: ADAPTIVE_PITCH,
    icon: DIAGNOSTIC_ICON,
    frontIcon: SKILL_PATH_ICON
  }
};

interface Props {
  variant: AdaptiveEmptyVariant;
  classroomId?: string;
  /** `'inline'` when the state sits inside one section of a page that has others. */
  layout?: 'stacked' | 'inline';
}

/** Nothing to show in any status — points the teacher to an adaptive subject to get started. */
export const AdaptiveOnboardingEmptyState = ({variant, classroomId, layout = 'stacked'}: Props) => {
  const {subjects, loading} = useAdaptiveEmptyStateSubjects(classroomId);
  const {title, body, icon, frontIcon} = VARIANT_COPY[variant];

  return (
    <EmptyState
      action={
        loading ? null : (
          <AdaptiveSubjectLinks
            align={layout === 'inline' ? 'start' : 'center'}
            subjects={subjects}
          />
        )
      }
      illustration={
        <EmptyIllustration
          badgeClassName='background-special-subtle content-special'
          frontIcon={frontIcon}
          icon={icon}
        />
      }
      layout={layout}
    >
      <div className='gap-x1 flex flex-col'>
        <Text as='div' size='l'>
          {title}
        </Text>
        <Text as='div' className='max-w-[440px]' color='secondary'>
          {body}
        </Text>
      </div>
    </EmptyState>
  );
};
